/**
 * RecoveryCenterPage — operator surface for Digital Employee runs that failed, were paused, or
 * are waiting on a human. Each card answers four questions in Hebrew: what failed, what was
 * preserved, what input/approval is needed, and whether a retry/resume is available.
 *
 * Route: /recovery
 * Access: agents_view (enforced server-side by the run list endpoints); the retry/resume
 * buttons additionally follow agents_run, the same permission POST /retry and POST /resume
 * enforce. Approvals are deliberately NOT duplicated here — a decision needs the full run
 * context and stays on the run page.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { agentRunsApi, agentsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { useNotification } from "../context/NotificationContext";
import { detailText } from "../utils/apiErrors";
import FailureRecoveryPanel from "../components/FailureRecoveryPanel";

const STATUS_LABELS = {
  failed:                         "נכשל",
  paused:                         "הופסק",
  awaiting_technician_review:     "ממתין לסקירה",
  awaiting_approval:              "ממתין לאישור",
  waiting_for_customer_connection:"ממתין לחיבור",
};

const STATUS_BADGE = {
  failed:                         "bg-red-100 text-red-700 border-red-200",
  paused:                         "bg-gray-100 text-gray-600 border-gray-300",
  awaiting_technician_review:     "bg-orange-100 text-orange-700 border-orange-200",
  awaiting_approval:              "bg-orange-100 text-orange-700 border-orange-200",
  waiting_for_customer_connection:"bg-blue-100 text-blue-700 border-blue-200",
};

const WINDOW_OPTIONS = [
  { value: 7,  label: "7 ימים אחרונים" },
  { value: 14, label: "14 ימים אחרונים" },
  { value: 30, label: "30 ימים אחרונים" },
];

function formatRecency(iso) {
  if (!iso) return null;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "ממש עכשיו";
  if (mins < 60) return `לפני ${mins} דק'`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `לפני ${hours} שע'`;
  return `לפני ${Math.floor(hours / 24)} ימים`;
}

function fullDate(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
}

function CountTile({ value, label, tone }) {
  return (
    <div className={`rounded-lg border px-4 py-2 text-center ${tone}`}>
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}

function RunCard({ run, canRun, retriedRun, onRetry, onResume, retryPending, resumePending }) {
  const sinceIso = run.finished_at || run.updated_at || run.created_at;
  const recency = formatRecency(sinceIso);
  const badge = STATUS_BADGE[run.status] ?? "bg-gray-100 text-gray-600 border-gray-200";

  const actions = !canRun ? null : run.status === "failed" ? (
    retriedRun ? (
      retriedRun.id ? (
        <Link to={`/agent-runs/${retriedRun.id}`} className="text-xs text-brand-600 hover:underline">
          הופעל ניסיון חוזר — מעבר לריצה החדשה ←
        </Link>
      ) : (
        <span className="text-xs text-green-700">הופעל ניסיון חוזר</span>
      )
    ) : (
      <button
        type="button"
        onClick={() => onRetry(run.id)}
        disabled={retryPending}
        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
      >
        {retryPending ? "מפעיל ניסיון חוזר…" : "נסה שוב"}
      </button>
    )
  ) : run.status === "paused" ? (
    <button
      type="button"
      onClick={() => onResume(run.id)}
      disabled={resumePending}
      className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
    >
      {resumePending ? "מפעיל מחדש…" : "המשך את הריצה"}
    </button>
  ) : null;

  return (
    <article data-testid={`recovery-card-${run.id}`} className="bg-white rounded-xl border shadow-sm p-4 space-y-3" dir="rtl">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-0.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {run.ticket_number ? (
              <Link
                to={`/tickets/${run.ticket_id}?from=${encodeURIComponent("/recovery")}`}
                className="text-sm font-semibold text-brand-600 hover:underline"
              >
                קריאה #{run.ticket_number}
              </Link>
            ) : (
              <span className="text-sm font-semibold text-gray-500">ריצה ללא קריאה</span>
            )}
            {run.ticket_priority === "high" && (
              <span className="text-xs text-red-600 font-semibold" title="עדיפות גבוהה">⚠ גבוהה</span>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate" title={run.ticket_title || undefined}>
            {run.ticket_title || "ללא כותרת"}
          </p>
          <p className="text-xs text-gray-400">
            {run.agent_name && (
              <Link to={`/agents/${run.agent_id}`} className="text-brand-600 hover:underline">{run.agent_name}</Link>
            )}
            {run.ticket_company_name && <span> · {run.ticket_company_name}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs border rounded-full px-2 py-0.5 font-medium ${badge}`}>
            {STATUS_LABELS[run.status] ?? run.status}
          </span>
          {recency && (
            <span className="text-xs text-gray-400" title={fullDate(sinceIso)}>{recency}</span>
          )}
        </div>
      </header>

      <FailureRecoveryPanel run={run} canRun={canRun} actions={actions} title={null} className="space-y-3" />

      <div className="text-xs">
        <Link
          to={`/agent-runs/${run.id}`}
          state={{ from: "/recovery" }}
          className="text-brand-600 hover:underline"
        >
          פתח את פרטי הריצה ←
        </Link>
      </div>
    </article>
  );
}

function Section({ title, hint, runs, error, emptyLabel, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        {!error && runs && <span className="text-xs text-gray-400">({runs.length})</span>}
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          לא ניתן לטעון את הרשימה — המצב אינו ידוע. נסה לרענן.
        </p>
      ) : runs && runs.length > 0 ? (
        <div className="space-y-3">{runs.map((run) => children(run))}</div>
      ) : runs ? (
        <p className="text-sm text-gray-400 bg-white border rounded-lg px-3 py-2">{emptyLabel}</p>
      ) : (
        <p className="text-sm text-gray-400">טוען…</p>
      )}
    </section>
  );
}

export default function RecoveryCenterPage() {
  const { can, loading: permissionsLoading, failed: permissionsFailed } = useAgentPermissions();
  const { addNotification } = useNotification();
  const qc = useQueryClient();
  const [sinceDays, setSinceDays] = useState(14);
  // Failed runs stay FAILED after a retry (the retry creates a detached new run), so the card
  // must remember that a retry was already started from here instead of offering a second one.
  const [retriedRuns, setRetriedRuns] = useState({});

  const canView = can("agents_view");
  const canRun = can("agents_run");
  const canManage = can("agents_manage");

  const listQuery = (status, limit) => ({
    queryKey: ["recoveryRuns", status, sinceDays],
    queryFn: () => agentRunsApi.listAll({ status, since_days: sinceDays, limit }),
    refetchInterval: 30000,
    enabled: canView,
  });

  const failedQuery = useQuery(listQuery("failed", 100));
  const pausedQuery = useQuery(listQuery("paused", 50));
  const reviewQuery = useQuery(listQuery("pending_review", 50));
  const connectionQuery = useQuery(listQuery("waiting_for_customer_connection", 50));
  // Crisis mode is what makes POST /retry answer 503; only agents_manage may read it, so the
  // banner is shown when known rather than promised when not.
  const { data: crisisMode } = useQuery({
    queryKey: ["recovery-crisis-mode"],
    queryFn: agentsApi.getCrisisMode,
    refetchInterval: 30000,
    enabled: canView && canManage,
  });

  const invalidateLists = () => qc.invalidateQueries({ queryKey: ["recoveryRuns"] });

  const retryMutation = useMutation({
    mutationFn: (runId) => agentRunsApi.retry(runId),
    onSuccess: (newRun, runId) => {
      addNotification("נוצרה ריצה חדשה לניסיון חוזר", "success");
      setRetriedRuns((prev) => ({ ...prev, [runId]: newRun || true }));
      invalidateLists();
      // The new run counts toward the agents' pending badges while it analyses.
      qc.invalidateQueries({ queryKey: ["agents-layout-badge"] });
    },
    onError: (err) => addNotification(detailText(err?.response?.data?.detail, "שגיאה בניסיון חוזר"), "error"),
  });

  const resumeMutation = useMutation({
    mutationFn: (runId) => agentRunsApi.resume(runId),
    onSuccess: () => {
      addNotification("הריצה הופעלה מחדש", "success");
      invalidateLists();
    },
    onError: (err) => addNotification(detailText(err?.response?.data?.detail, "שגיאה בהפעלה מחדש"), "error"),
  });

  const retryPendingId = retryMutation.isPending ? retryMutation.variables : null;
  const resumePendingId = resumeMutation.isPending ? resumeMutation.variables : null;

  const waitingRuns = useMemo(
    () => [...(reviewQuery.data || []), ...(connectionQuery.data || [])],
    [reviewQuery.data, connectionQuery.data]
  );

  // "?" when the list could not be loaded — a failed fetch must not read as a calming zero.
  const countOf = (query) => (query.isError ? "?" : query.data ? query.data.length : "…");
  const failedCount = countOf(failedQuery);
  const pausedCount = countOf(pausedQuery);
  const waitingCount = reviewQuery.isError || connectionQuery.isError
    ? "?"
    : !reviewQuery.data || !connectionQuery.data
    ? "…"
    : waitingRuns.length;
  const crisisActive = crisisMode?.active === true;

  if (permissionsLoading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  }
  if (permissionsFailed) {
    return <div role="alert" className="p-8 text-center text-red-600 text-sm">לא ניתן לטעון את ההרשאות שלך. נסה לרענן את הדף.</div>;
  }
  if (!canView) {
    return <div className="p-8 text-center text-gray-500">אין לך הרשאה לצפות בריצות AI.</div>;
  }

  const cardFor = (run) => (
    <RunCard
      key={run.id}
      run={run}
      canRun={canRun}
      retriedRun={retriedRuns[run.id] || null}
      onRetry={(runId) => retryMutation.mutate(runId)}
      onResume={(runId) => resumeMutation.mutate(runId)}
      retryPending={retryPendingId === run.id}
      resumePending={resumePendingId === run.id}
    />
  );

  return (
    <div className="space-y-6 max-w-4xl mx-auto" dir="rtl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-800">מרכז ההתאוששות</h1>
          <p className="text-sm text-gray-500">
            ריצות שנכשלו, הופסקו או ממתינות להחלטה — מה נכשל, מה נשמר, ומה הצעד הבא.
          </p>
        </div>
        <label className="text-xs text-gray-500 flex items-center gap-2">
          חלון זמן:
          <select
            value={sinceDays}
            onChange={(e) => setSinceDays(Number(e.target.value))}
            className="border rounded-lg px-2 py-1 text-xs text-gray-700"
          >
            {WINDOW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>

      {crisisActive && (
        <div role="alert" className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-800">
          מצב חירום פעיל — ניסיונות חוזרים ייחסמו עד לסיומו.
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <CountTile value={failedCount} label="נכשלו" tone="bg-red-50 border-red-200 text-red-700" />
        <CountTile value={pausedCount} label="הופסקו" tone="bg-gray-50 border-gray-200 text-gray-700" />
        <CountTile value={waitingCount} label="ממתינות לאדם" tone="bg-blue-50 border-blue-200 text-blue-700" />
      </div>

      <Section
        title="נכשלו — דורשות החלטה"
        hint="ניתן לנסות שוב רק ריצה שנכשלה"
        runs={failedQuery.data}
        error={failedQuery.isError}
        emptyLabel="אין ריצות שנכשלו בחלון הזמן הזה."
      >
        {cardFor}
      </Section>

      <Section
        title="הופסקו — ניתן להמשיך"
        hint="המשך דורש ניתוח שמור"
        runs={pausedQuery.data}
        error={pausedQuery.isError}
        emptyLabel="אין ריצות מושהות בחלון הזמן הזה."
      >
        {cardFor}
      </Section>

      <Section
        title="ממתינות לקלט או לחיבור"
        hint="אישור והחלטות מתבצעים בדף הריצה"
        runs={reviewQuery.isError || connectionQuery.isError ? undefined : waitingRuns}
        error={reviewQuery.isError || connectionQuery.isError}
        emptyLabel="אין ריצות שממתינות לאדם בחלון הזמן הזה."
      >
        {cardFor}
      </Section>
    </div>
  );
}
